import { isPresent } from '@ember/utils'
import { mustBeDefined } from './assert'
import type { BufferedChangeset } from 'ember-changeset/types'
import type { IntlService } from 'ember-intl'
import type { Log } from '@blakeelearning/log'
import type { JsonApiError } from 'teacher-ui-client/errors/json-api-errors'

// an error definition used to match a changeset error and handle it by attaching it to the changeset
type ChangesetErrorDefinition = {
  statusCode: number | string // http status code
  changeset: BufferedChangeset
  changesetProperty: string
  serverMessage?: string // to match an error message in the server response
  errorCode?: string // to match an error code in the server response
  ignore?: boolean
  currentSchoolId?: string
  kind: 'changeset'
} & TranslatableError

type TranslatableError =
  | {
      translationKey: string
    }
  | {
      errorTranslationFn: (error: unknown, errorDefinition: ErrorDefinition, intl: IntlService) => string
    }

// an error definition used to match an error and handle it with a callback
export interface CallbackErrorDefinition {
  statusCode: number | string
  serverMessage?: string
  callback: (error: unknown) => void
  kind: 'callback'
  ignore?: boolean
}

export type ErrorDefinition = ChangesetErrorDefinition | CallbackErrorDefinition

export interface ErrorHandlerError extends Partial<JsonApiError> {
  handled?: boolean
  [key: string]: unknown
}

/**
 * Utility class for handling errors for a feature, providing methods for:
 * - registering error definitions
 * - handling errors
 * - sending unhandled errors to a log service.
 *
 * @example ```ts
 * // to setup an error handler use the `errorHandler` service to create an
 * // errorHandler instance.
 * const errorHandler = this.errorHandlerService.create()
 *
 * // create definitions
 * errorHandler.registerErrorDefinition({
 *      statusCode: '422',
 *      serverMessage: 'login has already been taken',
 *      changesetProperty: 'login',
 *      changeset,
 *      translationKey: 'validation.message.teacher.loginTaken',
 *    })
 *
 * errorHandler.registerErrorDefinition({
 *      statusCode: '422',
 *      serverMessage: 'email has already been taken',
 *      changesetProperty: 'email',
 *      changeset,
 *      translationKey: 'validation.message.teacher.loginTaken',
 *    })
 *
 * // pass the server errors to the handle method
 * errorHandler.handle([{ detail: 'email is taken', status: 422 }])
 *
 * // optionally send unhandled errors to log
 * errorHandler.sendOtherErrorsToLog('unhandled error from component xyz')
 * ```
 */
export default class ErrorHandler {
  errors: ErrorHandlerError[] = []
  errorDefinitions: ErrorDefinition[] = []
  identifierKey: string
  intl: IntlService
  log: Log

  constructor(
    { intl, log }: { intl: IntlService; log: Log },
    identifierKey = 'detail', // this key maps to a property of the server error object. eg: detail, source
  ) {
    this.identifierKey = identifierKey
    this.intl = intl
    this.log = log
    this.errorDefinitions = []
  }

  /**
   * Register an error definition for this error handler instance.
   */
  registerErrorDefinition(errorDefinition: ErrorDefinition) {
    this.errorDefinitions.push(errorDefinition)
  }

  testIfAllErrorsWillBeHandled(errors: ErrorHandlerError[]) {
    return errors.every((error) =>
      this.errorDefinitions.some((definition) => this.errorMatchesDefinition(error, definition)),
    )
  }

  /**
   * Execute the error handler by running through all errors from the server and then try to find
   * an error definition which matches the error. Based on the type it will either execute a callback
   * or attach the error the given changeset and therefore invalidate for further form submissions
   */
  handle(errors: ErrorHandlerError[]) {
    mustBeDefined(errors, 'ErrorHandler::handle(errors)')
    this.errors = errors
    this.errors.forEach((error) => this.handleError(error))
  }

  private handleError(error: ErrorHandlerError) {
    this.errorDefinitions.forEach((definition) => {
      if (error.handled === true) return // next error
      if (this.errorMatchesDefinition(error, definition)) {
        error.handled = true
        this.processMatchedError(definition, error)
      }
    })
  }

  /**
   * Process a matched error by calling a callback or attaching the error to a changeset.
   */
  private processMatchedError(definition: ErrorDefinition, error: ErrorHandlerError) {
    if (definition.ignore) {
      // we are not doing anything
    } else if (definition.kind === 'callback') {
      definition.callback(error)
    } else if (definition.kind === 'changeset') {
      const propertyName = definition.changesetProperty
      const { changeset } = definition
      let message
      if ('errorTranslationFn' in definition) {
        message = definition.errorTranslationFn(error, definition, this.intl)
      } else {
        message = this.intl.t(definition.translationKey)
      }
      const isErrorAlreadyInChangeset = this.isErrorMessageInChangeset(propertyName, message, changeset)
      if (!isErrorAlreadyInChangeset) {
        changeset.pushErrors(propertyName, message)
      }
    }
  }

  hasUnhandledErrors() {
    return this.unhandledErrors.length > 0
  }

  /**
   * Sometimes we want to send errors which are unhandled to our log service.
   *
   * @param {String} message - A message to give those errors more meaning
   */
  sendOtherErrorsToLog(message: string) {
    this.unhandledErrors.forEach((error) => {
      this.log?.warning(`${message}: Status: ${error.status} - Detail: ${error.detail}`, error)
    })
  }

  /**
   * Returns all errors which have not been handled.
   */
  private get unhandledErrors() {
    return this.errors.filter((error) => !error.handled)
  }

  private isErrorMessageInChangeset(changesetPropertyKey: string, errorMessage: string, changeset: BufferedChangeset) {
    const errors = changeset.errors ?? []
    const errorsForField = errors.filter((error) => error.key === changesetPropertyKey)
    if (errorsForField.length === 0) return false
    return errorsForField[0].validation.indexOf(errorMessage) >= 0
  }

  private errorMatchesDefinition(error: ErrorHandlerError, errorDefinition: ErrorDefinition): boolean {
    const arePresentAndEqual = (a: unknown, b: unknown) => isPresent(a) && isPresent(b) && String(a) === String(b)

    const matchHttpStatusCode = arePresentAndEqual(error.status, errorDefinition.statusCode)
    const matchErrorCode = 'errorCode' in errorDefinition && arePresentAndEqual(error.code, errorDefinition.errorCode)
    const matchServerMessage = arePresentAndEqual(error[this.identifierKey], errorDefinition.serverMessage)

    return (
      // better to match on error code
      (matchHttpStatusCode && matchErrorCode) ||
      // settle for matching on server message
      (matchHttpStatusCode && matchServerMessage)
    )
  }
}
